import { useCallback, useEffect, useRef, useState } from 'react';
import { FiCalendar, FiFileText } from 'react-icons/fi';
import { AccountTableCard, AnalyticsFiltersBar, InfoTip, MetricCard, TrendChart, type TrendMetric } from '../../components/analytics';
import { SUBSCRIPTIONS_COLUMNS, SUBSCRIPTIONS_FORMATTERS } from '../../components/analytics/accountTables';
import { DEFAULT_FILTERS, GUIA_METRICAS_URL, isQueryable, useAnalyticsMeta, useAnalyticsResource } from '../../components/analytics/analyticsPage';
import { formatInstantDayBR } from '../../components/analytics/format';
import { METRIC_INFO } from '../../components/analytics/metricInfo';
import {
    buildChurnCards,
    buildComposition,
    buildGeneralCards,
    buildRevenueCards,
    subscriptionCardLabel,
    type SubscriptionCardView,
} from '../../components/analytics/subscriptionCards';
import {
    analyticsApi,
    type AnalyticsFilters,
    type AnalyticsSubscriptions,
    type ChurnTrendPoint,
    type RevenueTrendPoint,
    type SubscriptionBaseTrendPoint,
    type SubscriptionCardId,
} from '../../services/analytics';
import { AdminLayout } from './AdminLayout';
import styles from './AdminAnalytics.module.css';
import biz from './AnalyticsBusiness.module.css';

type TabId = 'geral' | 'receitas' | 'churn';

const TABS: Array<{ id: TabId; label: string }> = [
    { id: 'geral', label: 'Geral' },
    { id: 'receitas', label: 'Receitas' },
    { id: 'churn', label: 'Churn' },
];

type BaseMetric = 'pagantesAtivas' | 'contasComAcesso' | 'contasTotais';
const BASE_METRICS: TrendMetric<BaseMetric>[] = [
    { key: 'pagantesAtivas', label: 'Pagantes ativas', unit: 'int' },
    { key: 'contasComAcesso', label: 'Contas com acesso', unit: 'int' },
    { key: 'contasTotais', label: 'Contas totais', unit: 'int' },
];

type RevenueMetric = 'mrr' | 'arr';
const REVENUE_METRICS: TrendMetric<RevenueMetric>[] = [
    { key: 'mrr', label: 'MRR', unit: 'brl' },
    { key: 'arr', label: 'ARR', unit: 'brl' },
];

type ChurnMetric = 'cancelamentos' | 'churnPorContaPct';
const CHURN_METRICS: TrendMetric<ChurnMetric>[] = [
    { key: 'cancelamentos', label: 'Cancelamentos', unit: 'int' },
    { key: 'churnPorContaPct', label: 'Churn por conta', unit: 'pct' },
];

interface TabProps {
    filters: AnalyticsFilters;
    refreshKey: number;
    onRetry: () => void;
    tableCard: SubscriptionCardId | null;
    onShowCard: (card: SubscriptionCardId) => void;
    /** Periodo resolvido e, quando a aba sabe, o total de contas do recorte (barra de filtros). */
    onSummary: (summary: Summary) => void;
}

interface Summary {
    from: string;
    to: string;
    total?: number;
}

/** Repassa a barra de filtros o periodo e o total quando os dados chegam. */
function useSummary(onSummary: (s: Summary) => void, period: { from: string; to: string } | undefined, total?: number) {
    const from = period?.from;
    const to = period?.to;
    useEffect(() => {
        if (from && to) onSummary({ from, to, total });
    }, [from, to, total, onSummary]);
}

function Status({ loading, error, hasData, onRetry }: { loading: boolean; error: string | null; hasData: boolean; onRetry: () => void }) {
    if (error) {
        return (
            <div className={styles.error} role="alert">
                <span>{error}</span>
                <button type="button" className={styles.retry} onClick={onRetry}>
                    Tentar novamente
                </button>
            </div>
        );
    }
    if (loading && !hasData) {
        return (
            <div className={styles.loadingState} aria-live="polite">
                Carregando...
            </div>
        );
    }
    return null;
}

function CardsGrid({ cards, tableCard, onShowCard }: { cards: SubscriptionCardView[]; tableCard: SubscriptionCardId | null; onShowCard: (c: SubscriptionCardId) => void }) {
    return (
        <div className={biz.cardsGrid}>
            {cards.map((c, i) => (
                <MetricCard
                    key={c.id}
                    infoKey={c.id}
                    title={c.title}
                    value={c.value}
                    caption={c.caption}
                    highlight={c.highlight}
                    tipAlign={i === 0 ? 'start' : 'end'}
                    action={
                        c.tableCard
                            ? {
                                label: 'Ver contas na tabela',
                                onClick: () => onShowCard(c.tableCard as SubscriptionCardId),
                                active: tableCard === c.tableCard,
                            }
                            : undefined
                    }
                    footnote={c.noDetail ? 'Sem dados suficientes para detalhar' : undefined}
                />
            ))}
        </div>
    );
}

function Composition({ data }: { data: AnalyticsSubscriptions['composicao'] }) {
    const rows = buildComposition(data);
    return (
        <section className={biz.panelCard}>
            <header>
                <h4 className={biz.panelTitle}>
                    Composição atual das assinaturas
                    <InfoTip info={METRIC_INFO.composicaoAssinaturas} label="Composição atual das assinaturas" />
                </h4>
                <p className={biz.panelSubtitle}>Uma conta por cliente · na data final</p>
            </header>
            <ul className={biz.composition}>
                {rows.map((r) => (
                    <li key={r.id} className={biz.compositionRow}>
                        <span>{r.label}</span>
                        <span className={biz.compositionTrack} aria-hidden="true">
                            <span className={biz.compositionBar} style={{ width: `${Math.min(100, r.pct ?? 0)}%`, display: 'block' }} />
                        </span>
                        <span className={biz.compositionValue}>{r.text}</span>
                    </li>
                ))}
            </ul>
        </section>
    );
}

function GeneralTab({ filters, refreshKey, onRetry, tableCard, onShowCard, onSummary }: TabProps) {
    const [metric, setMetric] = useState<BaseMetric>('pagantesAtivas');
    const { data, loading, error } = useAnalyticsResource('subscriptions', analyticsApi.subscriptions, filters, refreshKey);
    useSummary(onSummary, data?.period, data?.composicao.total);
    return (
        <>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Situação das contas</h3>
                <p className={styles.sectionSubtitle}>Acompanhe o mix de planos e a exposição de contas em atraso.</p>
            </div>
            <Status loading={loading} error={error} hasData={!!data} onRetry={onRetry} />
            {data && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                    <CardsGrid cards={buildGeneralCards(data.cards)} tableCard={tableCard} onShowCard={onShowCard} />
                    <TrendChart<BaseMetric, SubscriptionBaseTrendPoint>
                        title="Evolução da base de assinantes"
                        infoKey="evolucaoAssinantes"
                        points={data.evolucao.points}
                        granularity={data.evolucao.granularity}
                        endDay={data.period.to}
                        metrics={BASE_METRICS}
                        metric={metric}
                        onMetricChange={setMetric}
                        getValue={(p, m) => p[m]}
                        loading={loading}
                    />
                    <Composition data={data.composicao} />
                </div>
            )}
        </>
    );
}

function RevenueTab({ filters, refreshKey, onRetry, tableCard, onShowCard, onSummary }: TabProps) {
    const [metric, setMetric] = useState<RevenueMetric>('mrr');
    const { data, loading, error } = useAnalyticsResource('revenue', analyticsApi.revenue, filters, refreshKey);
    useSummary(onSummary, data?.period);
    return (
        <>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Receitas</h3>
                <p className={styles.sectionSubtitle}>Separe receita recorrente projetada de pagamentos recebidos.</p>
            </div>
            <Status loading={loading} error={error} hasData={!!data} onRetry={onRetry} />
            {data && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                    <CardsGrid cards={buildRevenueCards(data.cards)} tableCard={tableCard} onShowCard={onShowCard} />
                    <TrendChart<RevenueMetric, RevenueTrendPoint>
                        title="Evolução da receita recorrente"
                        infoKey="evolucaoReceita"
                        points={data.evolucao.points}
                        granularity={data.evolucao.granularity}
                        endDay={data.period.to}
                        metrics={REVENUE_METRICS}
                        metric={metric}
                        onMetricChange={setMetric}
                        getValue={(p, m) => p[m]}
                        loading={loading}
                    />
                    <p className={biz.footnote}>
                        MRR e ARR são valores recorrentes projetados da base ativa. “Recebido no período” soma os pagamentos
                        confirmados no intervalo; a diferença entre esses valores não representa inadimplência por si só.
                    </p>
                </div>
            )}
        </>
    );
}

function ChurnTab({ filters, refreshKey, onRetry, tableCard, onShowCard, onSummary }: TabProps) {
    const [metric, setMetric] = useState<ChurnMetric>('cancelamentos');
    const { data, loading, error } = useAnalyticsResource('churn', analyticsApi.churn, filters, refreshKey);
    useSummary(onSummary, data?.period);
    return (
        <>
            <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>Cancelamentos e perda recorrente</h3>
                <p className={styles.sectionSubtitle}>
                    Entenda quantas contas saíram e o valor mensal recorrente associado. Expiração conta como cancelamento.
                </p>
            </div>
            <Status loading={loading} error={error} hasData={!!data} onRetry={onRetry} />
            {data && (
                <div className={`${styles.content} ${loading ? styles.contentLoading : ''}`} aria-busy={loading}>
                    <CardsGrid cards={buildChurnCards(data.cards, data.filtroDeStatus)} tableCard={tableCard} onShowCard={onShowCard} />
                    <TrendChart<ChurnMetric, ChurnTrendPoint>
                        title="Evolução dos cancelamentos"
                        infoKey="evolucaoCancelamentos"
                        points={data.evolucao.points}
                        granularity={data.evolucao.granularity}
                        endDay={data.period.to}
                        metrics={CHURN_METRICS}
                        metric={metric}
                        onMetricChange={setMetric}
                        getValue={(p, m) => p[m]}
                        loading={loading}
                    />
                    <p className={biz.footnote}>
                        O churn compara saídas com a base pagante no começo de cada período. O MRR cancelado expressa valor
                        mensalizado; receita perdida real depende de conciliação.
                    </p>
                </div>
            )}
        </>
    );
}

/** Assinaturas (Geral, Receitas, Churn): analise da base pagante, separada da tela de gestao de assinaturas. */
export function AdminSubscriptionAnalytics() {
    const [tab, setTab] = useState<TabId>('geral');
    const [filters, setFilters] = useState<AnalyticsFilters>(DEFAULT_FILTERS);
    const [reloadKey, setReloadKey] = useState(0);
    const [tableCard, setTableCard] = useState<SubscriptionCardId | null>(null);
    const [summary, setSummary] = useState<{ from: string; to: string; total: number | null } | null>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const { meta, timeZone, today } = useAnalyticsMeta();
    const queryable = isQueryable(filters);

    const handleFiltersChange = useCallback((next: AnalyticsFilters) => setFilters(next), []);
    const retry = useCallback(() => setReloadKey((k) => k + 1), []);
    const showCard = useCallback((card: SubscriptionCardId) => {
        setTableCard(card);
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, []);
    // O total de contas so vem da aba Geral; nas outras abas fica o ultimo conhecido.
    const handleSummary = useCallback(
        (s: Summary) => setSummary((prev) => ({ from: s.from, to: s.to, total: s.total ?? prev?.total ?? null })),
        [],
    );

    const tabProps: TabProps = {
        filters,
        refreshKey: reloadKey,
        onRetry: retry,
        tableCard,
        onShowCard: showCard,
        onSummary: handleSummary,
    };

    return (
        <AdminLayout>
            <div className={styles.pageHeader}>
                <h2 className={styles.pageTitle}>Assinaturas</h2>
                <div className={styles.headerRight}>
                    <a className={styles.guideLink} href={GUIA_METRICAS_URL} target="_blank" rel="noopener noreferrer">
                        <FiFileText size={14} aria-hidden="true" />
                        Guia das métricas (PDF)
                    </a>
                    <span className={styles.today}>
                        <FiCalendar size={14} aria-hidden="true" />
                        {formatInstantDayBR(new Date().toISOString(), timeZone)}
                    </span>
                </div>
            </div>

            <AnalyticsFiltersBar
                filters={filters}
                onChange={handleFiltersChange}
                segmentos={meta?.filters.segmentos ?? []}
                faturamentos={meta?.filters.faturamentos ?? []}
                totalContas={summary?.total ?? null}
                range={summary ? { from: summary.from, to: summary.to } : null}
                maxDay={today}
            />

            <div className={styles.tabs} role="tablist" aria-label="Seções de assinaturas">
                {TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        id={`assinaturas-tab-${t.id}`}
                        aria-selected={tab === t.id}
                        aria-controls={`assinaturas-panel-${t.id}`}
                        className={`${styles.tab} ${tab === t.id ? styles.tabActive : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div
                role="tabpanel"
                id={`assinaturas-panel-${tab}`}
                aria-labelledby={`assinaturas-tab-${tab}`}
                className={styles.panel}
            >
                {!queryable && <p className={styles.notice}>Informe as duas datas do período personalizado.</p>}

                {tab === 'geral' && <GeneralTab {...tabProps} />}
                {tab === 'receitas' && <RevenueTab {...tabProps} />}
                {tab === 'churn' && <ChurnTab {...tabProps} />}

                <div ref={tableRef} className={styles.sectionGap}>
                    <AccountTableCard
                        name="assinaturas-por-cliente"
                        title="Assinaturas por cliente"
                        subtitle="Situação na data final do período"
                        params={{ card: tableCard ?? undefined }}
                        headerAction={
                            tableCard && (
                                <span className={biz.tableFilter}>
                                    Filtrado por: {subscriptionCardLabel(tableCard)}
                                    <button type="button" className={biz.clearFilter} onClick={() => setTableCard(null)}>
                                        Mostrar todas
                                    </button>
                                </span>
                            )
                        }
                        columns={SUBSCRIPTIONS_COLUMNS}
                        formatters={SUBSCRIPTIONS_FORMATTERS}
                        exportFilename="analytics-assinaturas-por-cliente.xlsx"
                        emptyMessage="Nenhuma conta neste recorte."
                        filters={filters}
                        enabled={queryable}
                        refreshKey={reloadKey}
                        timeZone={timeZone}
                    />
                </div>
            </div>
        </AdminLayout>
    );
}
